import { useEffect, useState } from "react"

import {
  type Loaded,
  getTrackingFilterOptions,
  getTrackingStats,
  getTrackingSummary
} from "../api/tracking.services.ts"
import type {
  TrackingFilterOptions,
  TrackingQuery,
  TrackingSummary,
  TrackingTile
} from "../types/tracking.types.ts"

export interface Loadable<T> {
  status: "loading" | "loaded" | "error"
  data: T | null
}

// An answer of the wrong shape is a failed load: the panels it feeds say so rather than crash.
function useLoaded<T>(
  load: () => Promise<Loaded<T>>,
  key: string,
  isShaped: (data: T) => boolean
): Loadable<T> {
  const [state, setState] = useState<Loadable<T>>({
    status: "loading",
    data: null
  })
  useEffect(() => {
    let cancelled = false
    setState((previous) => ({ status: "loading", data: previous.data }))
    void load().then((result) => {
      if (cancelled) return
      setState(
        result.ok && result.data && isShaped(result.data)
          ? { status: "loaded", data: result.data }
          : { status: "error", data: null }
      )
    })
    return () => {
      cancelled = true
    }
    // `key` stands for whatever `load` closes over.
  }, [key])
  return state
}

/** The tiles take no filters: they are the baseline the table is read against. */
export function useTrackingStats(
  team?: string,
  refreshKey?: string
): Loadable<TrackingTile[]> {
  const stats = useLoaded(
    () => getTrackingStats(team ? { team } : {}),
    `stats:${team ?? ""}:${refreshKey ?? ""}`,
    (data) => Array.isArray(data.tiles)
  )
  return { status: stats.status, data: stats.data?.tiles ?? null }
}

/**
 * The strip follows the table's filters (R15.3e); the order is irrelevant to it.
 * `generation` moves whenever the table reloads, so a resend or void shows here too.
 */
export function useTrackingSummary(
  query: TrackingQuery,
  generation = 0
): Loadable<TrackingSummary> {
  const { ordering: _ordering, ...filters } = query
  return useLoaded(
    () => getTrackingSummary(filters),
    `${JSON.stringify(filters)}:${generation}`,
    (data) =>
      Boolean(
        data.cycle_time &&
        data.time_to_first_open &&
        data.our_delay &&
        data.open_rate &&
        data.decline_rate
      )
  )
}

export function useTrackingFilterOptions(
  team?: string
): Loadable<TrackingFilterOptions> {
  return useLoaded(
    () => getTrackingFilterOptions(team ? { team } : {}),
    `options:${team ?? ""}`,
    (data) => Array.isArray(data.senders)
  )
}
